import React, { createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex, HStack, useBreakpointValue } from '@chakra-ui/react';
import { House } from '@/client';
import { useHouseContext } from './HouseContext';
import { useHousePaymentStatus } from '@/hooks/payment/useHousePaymentStatus';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader';
import { HousePaymentRequired } from '@/components/payment/HousePaymentRequired';
import PropertySelector from '@/components/properties/selector/PropertySelector.tsx';
import { GuestAccessIndicator } from '@/components/access/GuestAccessIndicator.tsx';
import { Roles } from '@/utils/roleUtils.ts';
import { useAcceptInvitation } from '@/hooks/sharing/useAcceptInvitation.ts';
import ShareButton from '@/components/dashbaord/sharing/ShareButton.tsx';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { useIsPropertyLocked } from '@/hooks/properties/usePropertyPaywallState';
import { useNavigate } from '@tanstack/react-router';
import { Routes } from '@/routes';
import { makeLogger } from '@/utils/logger/logger';
import { useDesktopPropertyHeaderPortalContainer } from '@/context/DesktopPropertyHeaderPortalContext';
import { splitAddress } from '@/utils/properties/addressUtils.ts';

const logger = makeLogger('CurrentHouseContext');

/**
 * Context for components that always operate within a house context
 * Guarantees the existence of a selected house, eliminating the need for null checks
 */
interface CurrentHouseContextType {
	house: House; // Note: Not nullable
	houseId: number;
	isHousePaid: boolean;
	// Server-provided permissions for the current user in this house
	permissions: readonly string[];
}

const CurrentHouseContext = createContext<CurrentHouseContextType | undefined>(undefined);

/**
 * Provider component that guarantees a selected house exists
 * Only renders its children when a house is selected
 */
export const CurrentHouseProvider: React.FC<{
	children: React.ReactNode;
	fallback?: React.ReactNode;
	showPropertySelector?: boolean;
}> = ({ children, fallback = <GlobalOverlayLoader text="Loading ..." />, showPropertySelector = true }) => {
	// Resolved on the first render: the default starts at `base` (mobile) everywhere, which would briefly mount the mobile portals on desktop.
	const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false });
	const { selectedHouse, isSelecting } = useHouseContext();
	const { isHousePaid, isPending: isPaymentPending, error } = useHousePaymentStatus(selectedHouse?.id);
	const isLocked = useIsPropertyLocked(selectedHouse?.id);
	const navigate = useNavigate();
	const desktopHeaderPortalContainer = useDesktopPropertyHeaderPortalContainer();
	const { canManageAccess } = useHousePermissions();
	useAcceptInvitation(selectedHouse);

	useEffect(() => {
		if (isLocked && selectedHouse) {
			logger.info(`House ${selectedHouse.id} is locked (paywall) — redirecting to /properties`);
			navigate({ to: Routes.PROPERTIES });
		}
	}, [isLocked, selectedHouse, navigate]);

	// Resolve portal container safely every render; only portal if container exists
	const mobilePortalContainer =
		typeof document !== 'undefined'
			? (document.getElementById('mobile-property-selector-portal') as HTMLElement | null)
			: null;
	const mobileInvitePortalContainer =
		typeof document !== 'undefined'
			? (document.getElementById('mobile-invite-button-portal') as HTMLElement | null)
			: null;
	if (isLocked) {
		return <GlobalOverlayLoader text="Redirecting..." />;
	}

	// While selection is being resolved and no house is selected yet, show a stable global loader
	// Note: HouseProvider already shows a loader during initial houses/user loading
	if (isSelecting || (isPaymentPending && !!selectedHouse)) {
		if (selectedHouse) {
			return <GlobalOverlayLoader text={`Loading ${splitAddress(selectedHouse.address).street}...`} />;
		} else {
			return <GlobalOverlayLoader text="Loading your properties ..." />;
		}
	}

	// After selection completes, if no house is selected, render the provided fallback
	if (!selectedHouse) {
		return <>{fallback}</>;
	}

	// Handle API errors - show an error message or fallback to assuming paid
	if (error) {
		console.error('Failed to check house payment status:', error);
		// For now, assume house is paid if API fails to avoid blocking access
		// In production, you might want to show an error message or retry
	}

	// Show payment required component if house is not paid
	if (!error && !isHousePaid) {
		return <HousePaymentRequired />;
	}

	const currentHouse = selectedHouse;
	const permissions = currentHouse.user_role?.permissions ?? [];

	const contextValue: CurrentHouseContextType = {
		house: currentHouse,
		houseId: currentHouse.id,
		isHousePaid,
		permissions,
	};

	const userRole = currentHouse.user_role;
	const isOwnerOrAdmin = userRole?.role === Roles.Owner || userRole?.role === Roles.Admin;
	const accessExpiresAt = isOwnerOrAdmin ? null : (userRole?.expires_at ?? null);

	function renderHouseAddressOrSelector() {
		if (isMobile) {
			// Mobile: the nav row owns spacing; keep this portal content compact.
			return (
				<Box w="full" minW={0}>
					<PropertySelector />
				</Box>
			);
		}
		// Desktop: use HStack and vertically center contents
		return (
			<HStack
				p={{ base: 4, md: 0 }}
				pt={{ base: 0, md: 2 }}
				pb={{ base: 0, md: 2 }}
				maxWidth={{ base: '360px', md: '600px' }}
				align="center"
			>
				{/* Just show the address without selector for tradesman users */}
				<PropertySelector />
				<Box pl={2}>{!isOwnerOrAdmin && <GuestAccessIndicator expiresAt={accessExpiresAt} />}</Box>
			</HStack>
		);
	}

	function renderDesktopHouseHeader() {
		return (
			<Flex
				className="house-property-selector-desktop"
				w="full"
				pl={3}
				pr={3}
				pb={0}
				zIndex={10}
				align="center"
				justify="space-between"
				bg="linear-gradient(to bottom, token(colors.surface.canvas) 0%, token(colors.surface.canvas) 72%, transparent 100%)"
			>
				{renderHouseAddressOrSelector()}
				{canManageAccess && <ShareButton houseId={currentHouse.id} />}
			</Flex>
		);
	}

	return (
		<CurrentHouseContext.Provider value={contextValue}>
			{/* Desktop PropertySelector via Portal */}
			{showPropertySelector && !isMobile && desktopHeaderPortalContainer
				? createPortal(renderDesktopHouseHeader(), desktopHeaderPortalContainer)
				: null}

			{/* Mobile PropertySelector via Portal. MobileNavBar stays mounted (CSS-hidden) on desktop, so its
			    containers exist there too: gate on the breakpoint or desktop mounts a second, hidden selector. */}
			{showPropertySelector && isMobile && mobilePortalContainer
				? createPortal(renderHouseAddressOrSelector(), mobilePortalContainer)
				: null}

			{showPropertySelector && isMobile && mobileInvitePortalContainer && canManageAccess
				? createPortal(<ShareButton houseId={currentHouse.id} />, mobileInvitePortalContainer)
				: null}

			{showPropertySelector && !isOwnerOrAdmin && (
				<Box display={{ base: 'block', md: 'none' }} w="full" color="fg.muted" textStyle="micro" px={4} pb={2}>
					<GuestAccessIndicator expiresAt={accessExpiresAt} />
				</Box>
			)}

			{children}
		</CurrentHouseContext.Provider>
	);
};

/**
 * Hook to access the current house context
 * Throws an error if used outside of a CurrentHouseProvider
 */
export const useCurrentHouse = () => {
	const context = useContext(CurrentHouseContext);
	if (context === undefined) {
		throw new Error('useCurrentHouse must be used within a CurrentHouseProvider');
	}
	return context;
};
